import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { motion, useAnimate } from "framer-motion";

type CardSpec = {
  id: 1 | 2 | 3 | 4;
  img: string;

  left?: number;
  top?: number;
  right?: number;
  bottom?: number;

  w: number;
  h: number;
  z: number;
  alwaysVisible?: boolean;
};

const cards: CardSpec[] = [
  {
    id: 1,
    img: "/assets/images/home/card3_1.png",
    right: 0,
    bottom: 0,
    w: 228,
    h: 196,
    z: 30,
    alwaysVisible: true,
  },
  {
    id: 4,
    img: "/assets/images/home/card3_2.png",
    left: 50.6,
    bottom: 59.37,
    w: 247.517,
    h: 124,
    z: 20,
  },
  {
    id: 3,
    img: "/assets/images/home/card3_3.png",
    left: 76.5,
    bottom: 213.37,
    w: 235,
    h: 164,
    z: 20,
  },
  {
    id: 2,
    img: "/assets/images/home/card3_4.png",
    right: 21.6,
    top: 151.63,
    w: 253.8987,
    h: 197.8643,
    z: 20,
  },
];

function resolveTopLeft(c: CardSpec, containerW: number, containerH: number) {
  const left = c.left ?? (c.right != null ? containerW - c.right - c.w : 0);
  const top = c.top ?? (c.bottom != null ? containerH - c.bottom - c.h : 0);
  return { left, top };
}

function centerOfResolved(c: CardSpec, containerW: number, containerH: number) {
  const { left, top } = resolveTopLeft(c, containerW, containerH);
  return { x: left + c.w / 2, y: top + c.h / 2 };
}

function curvePath(a: { x: number; y: number }, b: { x: number; y: number }, bend = 34) {
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;

  const dx = b.x - a.x;
  const dy = b.y - a.y;

  const len = Math.hypot(dx, dy) || 1;
  const nx = (-dy / len) * bend;
  const ny = (dx / len) * bend;

  const cx = mx + nx;
  const cy = my + ny;

  return `M ${a.x} ${a.y} Q ${cx} ${cy} ${b.x} ${b.y}`;
}

export default function HomeCard3_AbsoluteLayout() {
  //  기준 캔버스 크기
  const BASE_W = 623;
  const BASE_H = 604;

  //  반응형 wrapper 측정
  const outerRef = useRef<HTMLDivElement | null>(null);
  const [outerWidth, setOuterWidth] = useState(BASE_W);

  //  내부 캔버스(애니메이션/IO 대상)
  const [scope, animate] = useAnimate<HTMLDivElement>();
  const ioRef = useRef<IntersectionObserver | null>(null);

  //  wrapper 폭 측정
  useLayoutEffect(() => {
    const el = outerRef.current;
    if (!el) return;

    const ro = new ResizeObserver(([entry]) => {
      const w = entry?.contentRect?.width ?? BASE_W;
      setOuterWidth(w);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scale = useMemo(() => Math.min(outerWidth / BASE_W, 1), [outerWidth]);
  const scaledH = useMemo(() => BASE_H * scale, [scale]);

  //  내부 계산은 "기준 좌표계(BASE)"로 고정 (중요)
  const CONTAINER_W = BASE_W;
  const CONTAINER_H = BASE_H;

  const W = CONTAINER_W;
  const H = CONTAINER_H;

  const ring = {
    right: 71.5,
    bottom: 38.37,
    r: 160,
    pad: 12,
  } as const;

  const ringSize = (ring.r + ring.pad) * 2;

  const ringLeft =
    "left" in ring && (ring as any).left != null
      ? (ring as any).left
      : "right" in ring && (ring as any).right != null
      ? CONTAINER_W - (ring as any).right - ringSize
      : 0;

  const ringTop =
    "top" in ring && (ring as any).top != null
      ? (ring as any).top
      : "bottom" in ring && (ring as any).bottom != null
      ? CONTAINER_H - (ring as any).bottom - ringSize
      : 0;

  const pts = useMemo(() => {
    const c1 = cards.find((c) => c.id === 1)!;
    const c2 = cards.find((c) => c.id === 2)!;
    const c3 = cards.find((c) => c.id === 3)!;
    const c4 = cards.find((c) => c.id === 4)!;

    return {
      p1: centerOfResolved(c1, CONTAINER_W, CONTAINER_H),
      p4: centerOfResolved(c4, CONTAINER_W, CONTAINER_H),
      p3: centerOfResolved(c3, CONTAINER_W, CONTAINER_H),
      p2: centerOfResolved(c2, CONTAINER_W, CONTAINER_H),
    };
  }, [CONTAINER_W, CONTAINER_H]);

  const segs = useMemo(() => {
    return [
      { key: "14", d: curvePath(pts.p1, pts.p4, 32), toShowCard: 4 as const },
      { key: "43", d: curvePath(pts.p4, pts.p3, 26), toShowCard: 3 as const },
      { key: "32", d: curvePath(pts.p3, pts.p2, 32), toShowCard: 2 as const },
    ] as const;
  }, [pts]);

  useEffect(() => {
    let alive = true;
    const el = scope.current;
    if (!el) return;

    const run = async () => {
      // =========================
      // A) 카드 1~4 먼저 나오게 (1은 이미 보임)
      // =========================
      const EASE = [0.16, 1, 0.3, 1] as const;
      const INIT_Y = 8;
      const INIT_SCALE = 0.995;

      for (const id of [2, 3, 4] as const) {
        await animate(`.c3-${id}`, { opacity: 0, y: INIT_Y, scale: INIT_SCALE }, { duration: 0 });
      }

      // 링은 처음에 숨김 + 그리기 상태
      await animate(".ring-wrap", { opacity: 0 }, { duration: 0 });
      await animate(".ring-circle", { strokeDashoffset: 1200 }, { duration: 0 });

      // 선 초기화
      for (const s of segs) {
        await animate(`.seg-${s.key}`, { opacity: 0, strokeDashoffset: 2000 }, { duration: 0 });
      }

      const cardDur = 1.15;
      const cardGap = 0.1;

      await animate(".c3-2", { opacity: 1, y: 0, scale: 1 }, { duration: cardDur, ease: EASE });
      if (!alive) return;

      await animate(".c3-3", { opacity: 1, y: 0, scale: 1 }, { duration: cardDur, ease: EASE, delay: cardGap });
      if (!alive) return;

      await animate(".c3-4", { opacity: 1, y: 0, scale: 1 }, { duration: cardDur, ease: EASE, delay: cardGap });
      if (!alive) return;

      // =========================
      // C) 선 애니메이션 (링보다 먼저)
      // =========================
      const lineDur = 1.05;
      const GAP = 0.22;

      if (!alive) return;

      // =========================
      // B) 링(점선 원)은 마지막에 등장 + 그려지기
      // =========================
      await Promise.all([
        animate(".ring-wrap", { opacity: 1 }, { duration: 0.55, ease: EASE }),
        animate(".ring-circle", { strokeDashoffset: 0 }, { duration: 1.6, ease: EASE }),
      ]);

      if (!alive) return;

      // =========================
      // C) 선 애니메이션
      // =========================

      for (const s of segs) {
        if (!alive) return;

        await animate(`.seg-${s.key}`, { opacity: 1, strokeDashoffset: 0 }, { duration: lineDur, ease: EASE });

        await animate(el, {}, { duration: GAP });
      }
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          run();
          io.disconnect();
        }
      },
      { threshold: 0.2, rootMargin: "0px 0px -10% 0px" }
    );

    io.observe(el);

    return () => {
      alive = false;
      io.disconnect();
    };
  }, [animate, scope, segs]);

  return (
    <section className="w-full flex items-start">
      {/*  바깥 wrapper: w-full, 높이는 scaledH로 */}
      <div
        ref={outerRef}
        className="w-full max-w-[623px] rounded-[24px] overflow-hidden"
        style={{
          height: scaledH,
          position: "relative",
          background: "#F2F4F8",
          boxShadow: "0 1.813px 19.07px 0 rgba(48, 75, 109, 0.20)",
        }}>
        <img
          src="/assets/images/home/card_bg.png"
          alt=""
          draggable={false}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover", // 이미지가 꽉 차게
            pointerEvents: "none",
            userSelect: "none",
            zIndex: 0,
          }}
        />
        {/*  내부 캔버스: BASE_W/H 좌표계를 유지하고 scale로 줄임 */}
        <div
          ref={scope}
          style={{
            width: BASE_W,
            height: BASE_H,
            position: "absolute",
            left: 0,
            top: 0,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}>
          <style>{`
            @keyframes ringSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            .ringWrap {
              position: absolute;
              left: ${ringLeft}px;
              top: ${ringTop}px;
              width: ${ringSize}px;
              height: ${ringSize}px;
              pointer-events: none;
              z-index: 5;
              background: transparent !important;
              outline: none !important;
              box-shadow: none !important;
            }
            .ringSvg {
              width: 100%;
              height: 100%;
              background: transparent !important;
              outline: none !important;
              overflow: visible;
              display: block;
            }
            .ringG {
              transform-origin: 50% 50%;
              animation: ringSpin 9s linear infinite;
            }
          `}</style>

          {/*  링(원) */}

          <div className="ringWrap ring-wrap" aria-hidden>
            <svg className="ringSvg" viewBox={`0 0 ${ringSize} ${ringSize}`} xmlns="http://www.w3.org/2000/svg">
              <g className="ringG">
                <motion.circle
                  className="ring-circle"
                  cx={ringSize / 2}
                  cy={ringSize / 2}
                  r={ring.r}
                  fill="none"
                  stroke="rgba(17,24,39,0.22)"
                  strokeWidth={1}
                  strokeDasharray="6 7"
                  strokeLinecap="round"
                  initial={{ opacity: 1, strokeDashoffset: 1200 }}
                  style={{ strokeDashoffset: 1200 }}
                />
              </g>
            </svg>
          </div>

          {/*  선 레이어 */}
          <svg
            width={W}
            height={H}
            viewBox={`0 0 ${W} ${H}`}
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 15,
              pointerEvents: "none",
              background: "transparent",
            }}>
            {segs.map((s) => (
              <motion.path
                key={s.key}
                className={`seg-${s.key}`}
                d={s.d}
                fill="none"
                stroke="rgba(17,24,39,0.35)"
                strokeWidth={3}
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
                initial={{ opacity: 0, strokeDashoffset: 2000 }}
                style={{ strokeDasharray: 2000, strokeDashoffset: 2000 }}
              />
            ))}
          </svg>

          {/*  카드 레이어 */}
          {cards.map((c) => {
            const fixed = !!c.alwaysVisible;
            return (
              <motion.img
                key={c.id}
                className={`c3-${c.id}`}
                src={c.img}
                alt=""
                draggable={false}
                initial={fixed ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 14, scale: 0.98 }}
                style={{
                  position: "absolute",
                  left: c.left,
                  top: c.top,
                  right: c.right,
                  bottom: c.bottom,
                  width: c.w,
                  height: c.h,
                  objectFit: "cover",
                  zIndex: c.z,
                  borderBottomRightRadius: c.id === 1 ? 0 : 9.15,
                  borderBottomLeftRadius: c.id === 1 ? 0 : 9.15,
                  borderTopRightRadius: c.id === 1 ? 0 : 9.15,
                  borderTopLeftRadius: 9.15,
                  userSelect: "none",
                  boxShadow:
                    c.id === 1
                      ? "0 1.484px 15.612px 0 rgba(48, 75, 109, 0.20)"
                      : "0 1.382px 14.54px 0 rgba(48, 75, 109, 0.20)",
                }}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}
